import { ReactNode } from "react";
import type { PostEntry } from "./PostPlanBuilder";
import { USECASE_LABELS } from "./usecaseConfig";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { de } from "date-fns/locale/de";

registerLocale("de", de);

interface PostPlanEntryCardProps {
  entry: PostEntry;
  index: number;
  onSelectedChange: (selected: boolean) => void;
  onTextChange: (text: string) => void;
  onScheduledDateChange: (date: Date) => void;
  onUsecaseChange: (usecase: string | null) => void;
  ctaText?: string;
  children?: ReactNode;
}

export default function PostPlanEntryCard({
  entry,
  index,
  onSelectedChange,
  onTextChange,
  onScheduledDateChange,
  onUsecaseChange,
  ctaText,
  children,
}: PostPlanEntryCardProps) {
  return (
    <div className="post-plan-entry-card">
        <div className="post-plan-entry-header">
            <div className="post-plan-entry-title">
                <div className="post-plan-entry-title__top">
                <span className="post-plan-entry-number">
                    Post {index + 1}
                </span>

                {entry.usecase && (
                    <span className="post-usecase-badge">
                        {USECASE_LABELS[entry.usecase] ?? entry.usecase}
                    </span>
                )}

                {entry.postPotential && (
                    <span
                    className={`potential-badge potential-badge--${entry.postPotential}`}
                    >
                    {entry.postPotential === "high" && "Hoch"}
                    {entry.postPotential === "medium" && "Mittel"}
                    {entry.postPotential === "low" && "Niedrig"}
                    {entry.postPotential === "none" && "Keine"}
                    </span>
                )}
                </div>

                <span className="post-plan-entry-file">
                {entry.fileName}
                </span>
            </div>

            <label className="post-plan-select">
                <input
                type="checkbox"
                checked={entry.selected}
                onChange={(e) =>
                    onSelectedChange(e.target.checked)
                }
                />

                <span>Für Postplan</span>
            </label>
            </div>

        <div className="post-plan-entry-content">
            <div className="post-plan-entry-media">
            {entry.imageDataUrl ? (
                <img
                src={entry.imageDataUrl}
                alt={entry.fileName}
                className="post-plan-entry-image"
                />
            ) : entry.imageError ? (
                <div className="image-placeholder">
                {entry.imageError}
                </div>
            ) : null}
            </div>

            <div className="post-plan-entry-details">
            {entry.textError && !entry.text && (
                <div className="text-error">
                {entry.textError}
                </div>
            )}

            <textarea
                value={entry.text}
                onChange={(e) => onTextChange(e.target.value)}
                rows={6}
                placeholder="Post-Text..."
            />

           <div className="post-plan-meta">
                <label>
                    Geplantes Datum
                    <DatePicker
                        selected={entry.scheduledDate}
                        onChange={(date: Date | null) => {
                            if (date) {
                            onScheduledDateChange(date);
                            }
                        }}
                        locale="de"
                        showTimeSelect
                        timeIntervals={30}
                        timeCaption="Uhrzeit"
                        dateFormat="dd.MM.yyyy HH:mm"
                        timeFormat="HH:mm"
                        dayClassName={(date: Date) =>
                            date.getDay() === 4
                            ? "planner-calendar-thursday"
                            : ""
                        }
                        timeClassName={(date: Date) => {
                            const hour = date.getHours();

                            return hour >= 9 && hour < 12
                            ? "planner-calendar-post-time"
                            : "";
                    }}
                    />
                </label>
                <span className="planner-date-hint">
                Donnerstage sind bevorzugte Post-Tage.
                </span>

                <label>
                    Usecase
                    <select
                    value={entry.usecase ?? ""}
                    onChange={(e) =>
                        onUsecaseChange(
                        e.target.value === "" ? null : e.target.value
                        )
                    }
                    >
                    <option value="">Nicht zugewiesen</option>

                    <optgroup label="LinkedIn">
                        <option value="LinkedInAG">
                        LinkedIn · AG Serious Games
                        </option>
                        <option value="LinkedInGameDays">
                        LinkedIn · GameDays
                        </option>
                        <option value="LinkedInPersonal">
                        LinkedIn · Personal
                        </option>
                    </optgroup>

                    <optgroup label="News">
                        <option value="FirstSpiritAG">
                        News · AG Serious Games
                        </option>
                        <option value="FirstSpiritTU">
                        News · TU Darmstadt
                        </option>
                    </optgroup>
                    </select>
                </label>
                </div>

            {ctaText && (
                <div className="post-plan-cta">
                <span className="post-plan-cta__label">
                    Call to Action
                </span>
                <p>{ctaText}</p>
                </div>
            )}
            </div>
        </div>

       {children && (
            <div className="post-plan-entry-footer">
                {children}
            </div>
        )}
        </div>
  );
}